import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, Home } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Header = ({ onSearch, onStartSearch }) => {
  const { getItemCount } = useCart();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const searchBoxRef = useRef(null);
  const debounceRef = useRef(null);
  const hasScrolledOnCurrentSearchRef = useRef(false);

  const handleSearch = (e) => {
    e.preventDefault();

    if (onSearch) {
      onSearch(searchQuery);
    }

    if (searchQuery.trim() && onStartSearch) {
      onStartSearch();
    }

    setShowSuggestions(false);
  };

  const handleSuggestionClick = (product) => {
    setSearchQuery(product.name);
    setShowSuggestions(false);
    navigate(`/produto/${product.slug}`);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    hasScrolledOnCurrentSearchRef.current = false;

    if (onSearch) {
      onSearch('');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      setSuggestions([]);
      setShowSuggestions(false);
      hasScrolledOnCurrentSearchRef.current = false;
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setLoadingSuggestions(true);

        const response = await axios.get(
          `${API_URL}/products?active_only=true&search=${encodeURIComponent(trimmedQuery)}&limit=6`
        );

        setSuggestions(response.data || []);
        setShowSuggestions(true);

        if (onSearch) {
          onSearch(trimmedQuery);
        }

        if (!hasScrolledOnCurrentSearchRef.current && onStartSearch) {
          onStartSearch();
          hasScrolledOnCurrentSearchRef.current = true;
        }
      } catch (error) {
        console.error('Erro ao buscar sugestões:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, onSearch]);

  const renderSuggestions = () => {
    if (!showSuggestions || !searchQuery.trim()) return null;

    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden z-50">
        <div className="max-h-[340px] overflow-y-auto">
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-base font-semibold text-slate-700">
              Resultado da pesquisa
            </p>
          </div>

          {loadingSuggestions ? (
            <div className="px-4 py-6 text-sm text-slate-500">
              Buscando produtos...
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((product) => {
              const mainImage =
                product.images && product.images.length > 0
                  ? product.images[0].image_url
                  : 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=200';

              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => handleSuggestionClick(product)}
                  className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <img
                    src={mainImage}
                    alt={product.name}
                    className="w-12 h-12 rounded-md object-cover bg-slate-100 flex-shrink-0"
                  />

                  <div className="min-w-0">
                    <p className="text-base font-semibold text-slate-700 truncate">
                      {product.name}
                    </p>
                    {product.reference && (
                      <p className="text-sm text-slate-500 truncate">
                        Ref: {product.reference}
                      </p>
                    )}
                  </div>
                </button>
              );
            })
          ) : (
            <div className="px-4 py-6 text-sm text-slate-500">
              Nenhum produto encontrado.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-50 shadow-sm" style={{ backgroundColor: '#273e82' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img
              src="/logo.png"
              alt="Central de Ferragens"
              className="w-[180px] h-[70px]"
            />
          </Link>

          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full" ref={searchBoxRef}>
              <input
                type="text"
                placeholder="Buscar produtos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  if (searchQuery.trim() && suggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                className="w-full pl-12 pr-12 py-3 rounded-lg border border-white/20 focus:border-[#fef000] focus:ring-2 focus:ring-[#fef000]/30 outline-none transition-all placeholder:text-slate-400 bg-white"
                data-testid="search-input"
              />

              <Search
                className="absolute left-4 top-1/2 transform -translate-y-1/2"
                style={{ color: '#fef000' }}
                size={20}
              />

              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#273e82]"
                >
                  <X size={18} />
                </button>
              )}

              {renderSuggestions()}
            </div>
          </form>

          <div className="flex items-center gap-4">
            <Link
              to="/carrinho"
              className="relative p-2 rounded-full hover:bg-white/10 transition-colors group"
              data-testid="cart-button"
            >
              <ShoppingCart
                size={24}
                className="text-white group-hover:text-[#fef000] transition-colors"
              />
              {getItemCount() > 0 && (
                <span
                  className="absolute -top-1 -right-1 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  style={{ backgroundColor: '#fef000', color: '#273e82' }}
                  data-testid="cart-count"
                >
                  {getItemCount()}
                </span>
              )}
            </Link>
            <Link
              to="/"
              className="p-2 rounded-full hover:bg-white/10 transition-colors group"
              data-testid="home-button"
            >
              <Home
                size={24}
                className="text-white group-hover:text-[#fef000] transition-colors"
              />
            </Link>

            <button
              className="md:hidden p-2 rounded-full hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? (
                <X size={24} className="text-white" />
              ) : (
                <Menu size={24} className="text-white" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-4" data-testid="mobile-menu">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative" ref={searchBoxRef}>
                <input
                  type="text"
                  placeholder="Buscar produtos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => {
                    if (searchQuery.trim() && suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  className="w-full pl-12 pr-12 py-3 rounded-lg border border-white/20 focus:border-[#fef000] focus:ring-2 focus:ring-[#fef000]/30 outline-none transition-all bg-white"
                />

                <Search
                  className="absolute left-4 top-1/2 transform -translate-y-1/2"
                  style={{ color: '#fef000' }}
                  size={20}
                />

                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#273e82]"
                  >
                    <X size={18} />
                  </button>
                )}

                {renderSuggestions()}
              </div>
            </form>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;